import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { FilterSection } from "../../components/ResponseManagerV3/types";
import ResponseManagerV3Service from "../../services/ResponseManagerV3/ResponseManagerV3Service";

interface FilterMetadata {
  sections?: FilterSection[];
  [key: string]: any;
}

interface ResponseManagerV3FiltersStore {
  filtersMetadata: FilterMetadata;
  fetchedState: "LOADING" | "SUCCESS" | "ERROR" | null;
  currentFilters: any[];
  isFilterApplied: boolean;
  initialFilters: any;
  defaultFormData: any;
  // Dynamic filters management
  dynamicFilters: any;
  dynamicBaseline: any;
  hasDynamicDirty: boolean;
  // Track last jobId to detect job changes
  lastJobId: number | null;
  // Store current jobId and tabId for filter fetching
  currentJobId: number | null;
  currentTabId: string | null;

  // Actions
  setFiltersMetadata: (metadata: FilterMetadata) => void;
  setFetchedState: (state: "LOADING" | "SUCCESS" | "ERROR" | null) => void;
  updateDefaultFilters: (filters: any) => void;
  updateDefaultFormFilters: (filters: any) => void;
  setFilterApplied: (applied: boolean) => void;
  setInitialFilters: (filters: any) => void;
  resetFilters: () => void;
  // Dynamic filters actions
  setDynamicFilters: (filters: any) => void;
  updateDynamicFilter: (key: string, value: any) => void;
  setDynamicBaseline: (baseline: any) => void;
  setHasDynamicDirty: (dirty: boolean) => void;
  clearDynamicFilters: () => void;
  applyDynamicFilters: () => void;
  setLastJobId: (jobId: number | null) => void;
  setCurrentJobAndTab: (jobId: number | null, tabId?: string | null) => void;
  // Reset persisted state when job changes
  resetPersistedState: () => void;
  // Fetch filters with current values (updates counts/values from backend)
  fetchFiltersWithCurrentValues: (
    jobId: number,
    tabId?: string,
    updatedFilters?: any
  ) => Promise<void>;
}

const useResponseManagerV3FiltersStore =
  create<ResponseManagerV3FiltersStore>()(
    persist(
      (set, get) => ({
        filtersMetadata: {},
        fetchedState: null,
        currentFilters: [],
        isFilterApplied: false,
        initialFilters: {},
        defaultFormData: {},
        dynamicFilters: {},
        dynamicBaseline: {},
        hasDynamicDirty: false,
        lastJobId: null,
        currentJobId: null,
        currentTabId: null,

        setFiltersMetadata: (metadata: FilterMetadata) => {
          set({ filtersMetadata: metadata });
        },

        setFetchedState: (state: "LOADING" | "SUCCESS" | "ERROR" | null) => {
          set({ fetchedState: state });
        },

        updateDefaultFilters: (filters: any) => {
          set({
            currentFilters: Array.isArray(filters) ? filters : [filters]
          });
        },

        updateDefaultFormFilters: (filters: any) => {
          set({
            defaultFormData: filters,
            // Sync dynamicFilters and dynamicBaseline when defaultFormData is updated
            dynamicFilters: filters,
            dynamicBaseline: filters
          });
        },

        setFilterApplied: (applied: boolean) => {
          set({ isFilterApplied: applied });
        },

        setInitialFilters: (filters: any) => {
          set({ initialFilters: filters });
        },

        resetFilters: () => {
          set(state => ({
            currentFilters: [],
            isFilterApplied: false,
            defaultFormData: state.initialFilters,
            dynamicFilters: {},
            dynamicBaseline: {},
            hasDynamicDirty: false
          }));
        },

        // Dynamic filter management methods
        setDynamicFilters: (filters: any) => {
          set({ dynamicFilters: filters });

          // Check if dirty compared to baseline
          const state = get();
          const isDirty =
            JSON.stringify(filters) !== JSON.stringify(state.dynamicBaseline);
          set({ hasDynamicDirty: isDirty });
        },

        updateDynamicFilter: (key: string, value: any) => {
          set(state => {
            const updated = {
              ...state.dynamicFilters,
              [key]: value
            };

            // Check if dirty compared to baseline
            const isDirty =
              JSON.stringify(updated) !== JSON.stringify(state.dynamicBaseline);

            // Immediately fetch filters with updated values
            // This updates filter counts/values from backend
            if (state.currentJobId && state.filtersMetadata?.sections) {
              // Call async but don't wait - fire and forget
              // Pass the updated filters directly to avoid reading stale state
              state.fetchFiltersWithCurrentValues(
                state.currentJobId,
                state.currentTabId || undefined,
                updated
              );
            }

            return {
              dynamicFilters: updated,
              hasDynamicDirty: isDirty
            };
          });
        },

        setDynamicBaseline: (baseline: any) => {
          set({
            dynamicBaseline: baseline,
            hasDynamicDirty: false
          });
        },

        setHasDynamicDirty: (dirty: boolean) => {
          set({ hasDynamicDirty: dirty });
        },

        clearDynamicFilters: () => {
          const state = get();
          set({
            dynamicFilters: { ...state.dynamicBaseline },
            hasDynamicDirty: false
          });
        },

        applyDynamicFilters: () => {
          const state = get();
          // Update defaultFormData with applied filters (this gets persisted)
          set({
            defaultFormData: { ...state.dynamicFilters },
            dynamicBaseline: { ...state.dynamicFilters },
            hasDynamicDirty: false,
            isFilterApplied: true
          });

          // Transform dynamic filters to current filters format
          const transformedFilters =
            state.filtersMetadata.sections?.map(section => ({
              section: section.section,
              filters: section.filters.map((filter: any) => ({
                key: filter.key,
                selectedValue:
                  state.dynamicFilters[filter.key] ?? filter.selectedValue
              }))
            })) || [];

          set({ currentFilters: transformedFilters });
        },

        setLastJobId: (jobId: number | null) => {
          set({ lastJobId: jobId });
        },

        setCurrentJobAndTab: (jobId: number | null, tabId?: string | null) => {
          set({
            currentJobId: jobId,
            currentTabId: tabId || null
          });
        },

        resetPersistedState: () => {
          set({
            defaultFormData: {},
            dynamicFilters: {},
            dynamicBaseline: {},
            isFilterApplied: false,
            currentFilters: [],
            lastJobId: null
          });
        },

        // Fetch filters with current dynamic filter values
        // This updates the filters metadata with counts/values from backend
        fetchFiltersWithCurrentValues: async (
          jobId: number,
          tabId?: string,
          updatedFilters?: any
        ) => {
          try {
            const state = get();

            // Use passed updatedFilters if available, otherwise use current state
            // This prevents reading stale state when called from updateDynamicFilter
            const filtersToUse = updatedFilters ?? state.dynamicFilters;

            // Transform dynamic filters to current filters format for API
            const filtersToSend =
              state.filtersMetadata.sections?.map(section => ({
                section: section.section,
                filters: section.filters.map((filter: any) => ({
                  key: filter.key,
                  selectedValue:
                    filtersToUse[filter.key] ?? filter.selectedValue
                }))
              })) || [];

            // Call filters API with current filter values
            const filtersResponse =
              await ResponseManagerV3Service.getCMPFilters(
                jobId,
                tabId,
                filtersToSend
              );

            if (filtersResponse?.sections) {
              // Update filters metadata with response (contains updated counts/values)
              // The backend response includes the selected values we sent, plus updated counts
              set({
                filtersMetadata: {
                  sections: filtersResponse.sections
                }
              });
            }
          } catch (error) {
            console.error("Error fetching filters with current values:", error);
            // Don't set error state to avoid breaking the UI
          }
        }
      }),
      {
        name: "response-manager-v3-filters-storage",
        storage: createJSONStorage(() => sessionStorage),
        // Persist defaultFormData (like database-product) and related state
        partialize: state => ({
          defaultFormData: state.defaultFormData,
          isFilterApplied: state.isFilterApplied,
          currentFilters: state.currentFilters,
          lastJobId: state.lastJobId
        })
      }
    )
  );

// Helper function to wait for hydration using Zustand's built-in persist API
export const waitForFiltersHydration = async (): Promise<void> => {
  // On server (SSR), there's no sessionStorage - resolve immediately
  if (typeof window === "undefined") {
    return;
  }

  // Check if already hydrated FIRST
  if (useResponseManagerV3FiltersStore.persist.hasHydrated()) {
    return;
  }

  // Not yet hydrated - wait for hydration to complete
  return new Promise<void>(resolve => {
    const unsubFinishHydration =
      useResponseManagerV3FiltersStore.persist.onFinishHydration(() => {
        unsubFinishHydration();
        resolve();
      });
  });
};

export default useResponseManagerV3FiltersStore;
